import { nanoid } from 'nanoid/non-secure';
import { RxQueue } from 'rx-queue';
import {
  catchError,
  EMPTY,
  filter,
  fromEvent,
  mergeMap,
  type Observable,
  Subject,
  Subscription,
  tap,
  timeout,
} from 'rxjs';
import { type IntercomEvent, intercomEventSchema } from '@/models/events/intercom-event';
import { createLogger } from '@/utils/logger';

export function createIntercomManager() {
  return new IntercomManager();
}

export default class IntercomManager {
  readonly id = nanoid(4);
  readonly channel: string;

  readonly #broadcastChannel: BroadcastChannel;
  readonly #logger = createLogger('Intercom');
  #isReady = false;
  #queue$ = new RxQueue<IntercomEvent>();
  #event$ = new Subject<IntercomEvent>();
  #subscriptions: Subscription = new Subscription();

  constructor(channel = 'chat-intercom-channel') {
    this.channel = channel;
    this.#broadcastChannel = new BroadcastChannel(channel);
    this.#initialize();
  }

  get event$(): Observable<IntercomEvent> {
    return this.#event$.asObservable();
  }

  get isReady() {
    return this.#isReady;
  }

  dispatch(message: IntercomEvent) {
    const { success, data, error } = intercomEventSchema.safeParse(message);

    if (success) {
      this.#queue$.next(data);
    } else {
      this.#logger.error('Invalid message', this.id, message, error);
    }
  }

  dispatchAsync<K extends IntercomEvent['event']>(message: IntercomEvent, awaitEventNames: K[], timeoutMs = 15_000) {
    return new Promise<Extract<IntercomEvent, { event: K }>>((resolve, reject) => {
      const subscription = this.event$
        .pipe(
          filter((event): event is Extract<IntercomEvent, { event: K }> => {
            return awaitEventNames.includes(event.event as K);
          }),
          timeout(timeoutMs),
        )
        .subscribe({
          next: (event) => {
            subscription.unsubscribe();
            resolve(event);
          },
          error: (error) => {
            subscription.unsubscribe();
            reject(error);
          },
        });

      this.dispatch(message);
    });
  }

  #initialize() {
    const queue$$ = this.#queue$
      .pipe(
        tap((event) => {
          this.#logger.info('Message sent', this.id, event);
        }),
      )
      .subscribe({
        next: (message) => {
          this.#broadcastChannel.postMessage(message);
        },
      });

    const event$$ = fromEvent<MessageEvent>(this.#broadcastChannel, 'message')
      .pipe(
        tap((event) => {
          this.#logger.info('Message received', this.id, event.data);
        }),
        mergeMap((event) => intercomEventSchema.parseAsync(event.data)),
        catchError((error) => {
          this.#logger.fatal('Message parsing failed', this.id, error);
          return EMPTY;
        }),
      )
      .subscribe({
        next: (event) => {
          this.#event$.next(event);
        },
        error: (error) => {
          this.#logger.fatal(this.id, error);
        },
        complete: () => {
          this.#logger.info('channel closed', this.id);
        },
      });

    this.#subscriptions.add(queue$$);
    this.#subscriptions.add(event$$);
  }
}
